import type { Place } from '@daily/ui';

/**
 * Page addresses (owner, 4 October 2026). The UI names its pages as places; this is the one place that
 * spells them as addresses and reads them back:
 *
 * | Page | Address |
 * |---|---|
 * | Landing, sign in, sign up | `/` |
 * | Calendar on today's month (Home) | `/home` |
 * | Calendar on another month | `/2026-09` |
 * | A day | `/2026-10-01` |
 * | One member's post that day | `/2026-10-01/ayoub` |
 * | A profile | `/ayoub` |
 *
 * Handles start with a letter, so a date never reads as a handle. `home` is reserved for the calendar.
 */

const HANDLE = '[A-Za-z][A-Za-z0-9_]{2,29}';
const MONTH = /^\/(\d{4})-(\d{2})$/;
const DAY = new RegExp(`^/(\\d{4}-\\d{2}-\\d{2})(?:/(${HANDLE}))?$`);
const PROFILE = new RegExp(`^/(${HANDLE})$`);

/** A real calendar date, 'YYYY-MM-DD' (no 30 February). */
export function realDate(date: string): boolean {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d || m > 12) return false;
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** The address of a place. */
export function pathOf(place: Place): string {
  if (place.kind === 'home') return '/home';
  if (place.kind === 'month') return `/${place.month}`;
  if (place.kind === 'day') return `/${place.date}`;
  if (place.kind === 'post') return `/${place.date}/${place.handle}`;
  return `/${place.handle}`;
}

/**
 * The place an address names: `'landing'` for `/`, null for an address that names no page (it is shown as
 * Home). A trailing slash is ignored and handles are read in lower case, as the backend stores them.
 */
export function placeFromPath(pathname: string): Place | 'landing' | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === '/' || path === '') return 'landing';
  if (path.toLowerCase() === '/home') return { kind: 'home' };
  const month = MONTH.exec(path);
  if (month) return Number(month[2]) >= 1 && Number(month[2]) <= 12 ? { kind: 'month', month: `${month[1]}-${month[2]}` } : null;
  const day = DAY.exec(path);
  if (day?.[1]) {
    if (!realDate(day[1])) return null;
    return day[2] ? { kind: 'post', date: day[1], handle: day[2].toLowerCase() } : { kind: 'day', date: day[1] };
  }
  const profile = PROFILE.exec(path);
  return profile?.[1] ? { kind: 'profile', handle: profile[1].toLowerCase() } : null;
}

/**
 * The part of the query an address keeps from page to page: only the development switches that choose the
 * fixture group (`fixtures`, `fx.*`), so a reload keeps the same data. Production addresses carry none.
 */
export function keptSearch(search: string): string {
  const kept = [...new URLSearchParams(search)].filter(([key]) => key === 'fixtures' || key.startsWith('fx.'));
  return kept.length ? `?${new URLSearchParams(kept).toString()}` : '';
}
